import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Search, Send } from 'lucide-react';
import * as api from '../../api';
import { Btn, Input, RNLLoader, Select } from '../UI';
import Pagination from '../requisition/Pagination';
import { Pill } from '../materialTakeoff/MTOShared';
import { EDITABLE, MAPPING_STATUS, MAPPING_TYPES, asOptions, coverageLabel, exportXlsx, fmtQty, typeLabel } from './mmUtils';

const th = { padding: '10px 12px', fontSize: 10, fontWeight: 800, color: '#fff', background: '#0b318bff', border: '1px solid black',
             textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 };
const td = { padding: '9px 12px', fontSize: 12.5, borderBottom: '1px solid var(--border-light)', verticalAlign: 'top' };

export const StatusPill = ({ status }) => <Pill meta={MAPPING_STATUS[status || 'unmapped']} />;

// the materials of one mapping, shown under its register row
function MappingLines({ mappingId }) {
  const [m, setM] = useState(null);
  useEffect(() => { api.getMapping(mappingId).then(setM).catch(() => setM(false)); }, [mappingId]);
  if (m === null) return <div style={{ padding: 10, fontSize: 12, color: 'var(--text-secondary)' }}>Loading materials…</div>;
  if (!m) return <div style={{ padding: 10, fontSize: 12, color: '#991b1b' }}>Could not load this mapping.</div>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-base)' }}>
      <thead><tr>{['Material Code', 'Material', 'Category', 'UOM', 'Mapping Type', 'BOQ Coverage', 'Qty / BOQ Unit', 'Remarks'].map(h => (
        <th key={h} style={{ ...th, position: 'static', background: '#334155', fontSize: 9.5 }}>{h}</th>))}</tr></thead>
      <tbody>
        {m.lines.map(x => (
          <tr key={x.id}>
            <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700 }}>{x.material_code}</td>
            <td style={td}>{x.material_name}</td>
            <td style={td}>{x.material_category || '—'}</td>
            <td style={td}>{x.material_uom || '—'}</td>
            <td style={td}>{typeLabel(x.mapping_type)}</td>
            <td style={td}>{coverageLabel(x.boq_coverage)}</td>
            <td style={{ ...td, textAlign: 'right' }}>{fmtQty(x.quantity_per_unit)}</td>
            <td style={td}>{x.remarks || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function MMRegister({ projectId, lists, openMapper, canEdit, toast, initialStatus, mine }) {
  const [f, setF] = useState({ division: '', category: '', subsection: '', status: initialStatus || '', type: '', search: '' });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState({ skip: 0, limit: 50 });
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState({});
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [waitingForMe, setWaitingForMe] = useState(null);    // mapping ids at my approval step, when filtering on them
  const [exporting, setExporting] = useState(false);

  const params = useCallback((extra = {}) => ({
    project_id: projectId, project_division_id: f.division, dmr_category_id: f.category, dmr_subsection_id: f.subsection,
    status: f.status, mapping_type: f.type, search: query,
    mapping_ids: waitingForMe ? waitingForMe.join(',') : undefined, ...extra,
  }), [projectId, f, query, waitingForMe]);

  const load = useCallback(() => {
    setRows(null);
    api.listMappingRegister(params({ skip: page.skip, limit: page.limit }))
      .then(r => { setRows(r || []); setTotal(r?.totalCount ?? (r || []).length); })
      .catch(e => { setRows([]); toast?.show?.(e.message || 'Could not load the register', 'error'); });
  }, [params, page, toast]);

  useEffect(() => { load(); }, [load]);

  const toggleWaiting = async () => {
    if (waitingForMe) { setWaitingForMe(null); return; }
    try {
      const inbox = await api.getMyInbox();
      setWaitingForMe((inbox || []).filter(i => i.entity_type === 'boq_material_mapping').map(i => i.entity_id));
    } catch (e) {
      toast?.show?.(e.message || 'Could not read your approval queue', 'error');
    }
    setPage(p => ({ ...p, skip: 0 }));
  };
  useEffect(() => { if (mine) toggleWaiting(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (v) => { setF(prev => ({ ...prev, [k]: v })); setPage(p => ({ ...p, skip: 0 })); };
  const pickedIds = Object.keys(picked).filter(k => picked[k]);

  const submitPicked = async () => {
    setBusy(true);
    try {
      const r = await api.submitMappings(pickedIds);
      api.clearApiCache();
      const ok = (r.submitted || []).length, bad = r.failed || [];
      toast?.show?.(`${ok} mapping(s) submitted${bad.length ? `; ${bad.length} not: ${bad[0].error}` : ''}`, bad.length ? 'error' : 'success');
      setPicked({});
      load();
    } catch (e) {
      toast?.show?.(e.message || 'Could not submit', 'error');
    }
    setBusy(false);
  };

  const exportAll = async () => {
    setExporting(true);
    try {
      const all = await api.listMappingRegister(params({ skip: 0, limit: 5000 }));
      exportXlsx(all || [], [
        { key: 'mapping_ref', label: 'Mapping Ref' }, { key: 'building', label: 'Building' }, { key: 'discipline', label: 'Discipline' },
        { key: 'work_package', label: 'Work Package' }, { key: 'boq_code', label: 'BOQ Code' }, { key: 'description', label: 'BOQ Description' },
        { key: 'quantity', label: 'BOQ Qty', value: r => Number(r.quantity) }, { key: 'unit_of_measure', label: 'UOM' },
        { key: 'material_count', label: 'Materials' }, { key: 'status', label: 'Status', value: r => MAPPING_STATUS[r.status || 'unmapped'].label },
        { key: 'mapping_types', label: 'Mapping Types', value: r => (r.mapping_types || []).map(typeLabel).join(', ') },
        { key: 'prepared_by', label: 'Mapped By' }, { key: 'date_prepared', label: 'Mapped Date' },
      ], 'BOQ-Material-Mapping-Register', 'Register');
    } catch (e) {
      toast?.show?.(e.message || 'Export failed', 'error');
    }
    setExporting(false);
  };

  const statusOptions = [{ value: '', label: 'All statuses' }, { value: 'unmapped', label: 'Not mapped yet' },
    { value: 'in_progress', label: 'Mapping in progress (not approved)' },
    ...Object.entries(MAPPING_STATUS).filter(([k]) => k !== 'unmapped').map(([value, m]) => ({ value, label: m.label }))];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>BOQ–Material Mapping Register</h3>
          <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
            Master relationship layer: every BOQ line → its work package → the materials that execute it. Open a line to map it.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn variant={waitingForMe ? 'primary' : 'secondary'} onClick={toggleWaiting}>
            {waitingForMe ? `Waiting for me (${waitingForMe.length}) ✕` : 'Waiting for my approval'}
          </Btn>
          <Btn variant="secondary" onClick={exportAll} loading={exporting}><Download size={14} /> Export Excel</Btn>
          {canEdit && pickedIds.length > 0 && (
            <Btn onClick={submitPicked} loading={busy}><Send size={14} /> Submit selected ({pickedIds.length})</Btn>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: 12 }}>
        <Select label="Building" value={f.division} onChange={set('division')} options={asOptions(lists.divisions, 'project_division_id', 'All buildings')} />
        <Select label="Discipline" value={f.category} onChange={set('category')} options={asOptions(lists.categories, 'id', 'All disciplines')} />
        <Select label="Work Package" value={f.subsection} onChange={set('subsection')} options={asOptions(lists.subsections, 'id', 'All work packages')} />
        <Select label="Mapping Type" value={f.type} onChange={set('type')} options={[{ value: '', label: 'All mapping types' }, ...MAPPING_TYPES]} />
        <Select label="Status" value={f.status} onChange={set('status')} options={statusOptions} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <Input value={f.search} onChange={set('search')} placeholder="Search BOQ code, description, mapping ref, material code or name…"
                 onKeyDown={e => { if (e.key === 'Enter') setQuery(f.search.trim()); }} />
        </div>
        <Btn onClick={() => { setQuery(f.search.trim()); setPage(p => ({ ...p, skip: 0 })); }}><Search size={14} /> Search</Btn>
      </div>

      {rows === null ? <RNLLoader title="Loading register..." subtitle="Reading BOQ lines" /> : (
        <>
          <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 30 }} />
                  {canEdit && <th style={{ ...th, width: 30 }} />}
                  {['Mapping Ref', 'Building', 'Discipline', 'Work Package', 'BOQ Code', 'BOQ Description', 'BOQ Qty', 'UOM', 'Materials', 'Status', 'Action'].map(h => (
                    <th key={h} style={{ ...th, textAlign: ['BOQ Qty', 'Materials'].includes(h) ? 'right' : 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={13} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>
                    {waitingForMe ? 'Nothing is waiting for your approval.' : 'No BOQ lines match these filters.'}
                  </td></tr>
                )}
                {rows.map(r => {
                  const isOpen = !!open[r.boq_item_id];
                  const canSubmit = r.mapping_id && EDITABLE.includes(r.status) && r.material_count > 0;
                  return (
                    <React.Fragment key={r.boq_item_id}>
                      <tr style={{ background: isOpen ? 'rgba(11,49,139,0.04)' : undefined }}>
                        <td style={td}>
                          {r.mapping_id && (
                            <button onClick={() => setOpen(o => ({ ...o, [r.boq_item_id]: !isOpen }))} title="Show materials"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0b318b', padding: 0 }}>
                              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                          )}
                        </td>
                        {canEdit && (
                          <td style={td}>
                            {canSubmit && <input type="checkbox" checked={!!picked[r.mapping_id]} title="Select to submit"
                                                 onChange={e => setPicked(p => ({ ...p, [r.mapping_id]: e.target.checked }))} />}
                          </td>
                        )}
                        <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#0b318b', whiteSpace: 'nowrap' }}>{r.mapping_ref || '—'}</td>
                        <td style={td}>{r.building || '—'}</td>
                        <td style={td}>{r.discipline || '—'}</td>
                        <td style={td}>{r.work_package || '—'}</td>
                        <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.boq_code}</td>
                        <td style={{ ...td, minWidth: 260, maxWidth: 420 }}>{r.description}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{fmtQty(r.quantity)}</td>
                        <td style={td}>{r.unit_of_measure || '—'}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{r.material_count}</td>
                        <td style={td}>
                          <StatusPill status={r.status} />
                          {r.currently_with && <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginTop: 3 }}>with {r.currently_with}</div>}
                        </td>
                        <td style={td}>
                          <Btn size="sm" variant={r.mapping_id ? 'secondary' : 'primary'} style={{ padding: '5px 12px', fontSize: 12 }}
                               onClick={() => openMapper(r.boq_item_id)}>
                            {!r.mapping_id ? (canEdit ? 'Map' : 'View') : EDITABLE.includes(r.status) && canEdit ? 'Edit' : 'Open'}
                          </Btn>
                        </td>
                      </tr>
                      {isOpen && r.mapping_id && (
                        <tr><td colSpan={13} style={{ padding: '0 0 10px 40px', borderBottom: '1px solid var(--border-light)' }}><MappingLines mappingId={r.mapping_id} /></td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12 }}>
            <Pagination total={total} skip={page.skip} limit={page.limit} onChange={setPage} />
          </div>
        </>
      )}
    </div>
  );
}
